(() => {
  const VISITOR_KEY = "coord-studio-visitor-v1";
  const bootstrapRequests = new WeakMap();
  const previewRequests = new Map();
  const editorModules = new Map();
  const stylesheetRequests = new Map();
  const VALID_RECIPE_IDS = new Set(["quiet-edit", "gallery-portrait", "mode-dispatch", "tokyo-issue", "glasshouse-diary", "collectors-paper", "rose-cafe", "boutique-lookbook", "noir-atelier", "egl-archive", "shop-the-edit", "pinboard-story"]);
  setupAll(document);
  if (document.documentElement.dataset.coordStudioSectionListener !== "true") {
    document.documentElement.dataset.coordStudioSectionListener = "true";
    document.addEventListener("shopify:section:load", (event) => setupAll(event.target));
  }

  function setupAll(scope) {
    if (scope instanceof Element && scope.matches("[data-coord-studio-launcher]")) setup(scope);
    scope.querySelectorAll?.("[data-coord-studio-launcher]").forEach((root) => setup(root));
  }

  function visitorId() {
    try {
      const current = localStorage.getItem(VISITOR_KEY);
      if (current) return current;
      const next = crypto.randomUUID();
      localStorage.setItem(VISITOR_KEY, next);
      return next;
    } catch {
      return crypto.randomUUID();
    }
  }

  function readSeed(root) {
    const node = root.querySelector("script[data-cs-seed]");
    if (!node?.textContent) return null;
    try { return JSON.parse(node.textContent); } catch { return null; }
  }

  function classifyLogo(image) {
    if (!(image instanceof HTMLImageElement)) return;
    const apply = () => {
      const ratio = image.naturalHeight > 0 ? image.naturalWidth / image.naturalHeight : 0;
      image.classList.toggle("cslr-logo--square", ratio >= .8 && ratio <= 1.25);
    };
    if (image.complete) apply();
    else image.addEventListener("load", apply, { once: true });
  }

  function renderPreview(root, items) {
    const featured = [...(items || [])]
      .sort((a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured)) || (b.priority || 0) - (a.priority || 0))
      .filter((item) => item.imageUrl)
      .slice(0, 3);
    root.querySelectorAll("[data-csl-piece]").forEach((slot, index) => {
      const item = featured[index];
      const image = slot.querySelector("img");
      if (!item || !image || (image.getAttribute("src") && slot.dataset.previewSlot !== "error")) return;
      if (slot.dataset.previewSlot === "error") image.removeAttribute("src");
      image.src = item.imageUrl;
      image.alt = "";
      slot.querySelector("span").textContent = item.category || root.dataset.messagePiece || "Piece";
      slot.querySelector("strong").textContent = item.title || "";
    });
  }

  function watchPreviewImage(root, slot, image) {
    if (image.dataset.cslPreviewWatched === "true") return;
    image.dataset.cslPreviewWatched = "true";
    const refresh = () => syncPreviewState(root);
    image.addEventListener("load", refresh);
    image.addEventListener("error", refresh);
  }

  function syncPreviewState(root, outcome) {
    if (outcome) root.dataset.previewOutcome = outcome;
    let ready = 0;
    let loading = 0;
    let errors = 0;
    root.querySelectorAll("[data-csl-piece]").forEach((slot) => {
      const image = slot.querySelector("img");
      const source = image?.getAttribute("src");
      let state = "empty";
      if (image && source) {
        watchPreviewImage(root, slot, image);
        state = image.complete ? (image.naturalWidth > 0 ? "ready" : "error") : "loading";
      }
      slot.dataset.previewSlot = state;
      if (state === "ready") {
        ready += 1;
        slot.dataset.previewOrder = String(ready);
      } else {
        delete slot.dataset.previewOrder;
      }
      const caption = slot.querySelector("figcaption");
      if (caption) caption.hidden = state !== "ready";
      if (state === "loading") loading += 1;
      if (state === "error") errors += 1;
    });
    root.dataset.previewCount = String(ready);
    if (loading > 0) root.dataset.previewState = "loading";
    else if (ready === 3) root.dataset.previewState = "ready";
    else if (ready > 0) root.dataset.previewState = "partial";
    else if (errors > 0 || root.dataset.previewOutcome === "unavailable") root.dataset.previewState = "unavailable";
    else root.dataset.previewState = "empty";
  }

  function renderBootstrap(root, data) {
    data = {
      ...data,
      settings: {
        ...(data.settings || {}),
        ...(root.dataset.logoUrl ? { logoUrl: root.dataset.logoUrl } : {}),
        ...(root.dataset.accent ? { brandColor: root.dataset.accent } : {}),
      },
    };
    root.__coordStudioBootstrap = data;
    root.dataset.displayFont = root.dataset.displayFont || data.settings?.displayFont || "editorial";
    const name = data.settings?.displayName || root.dataset.shopName || "Coord Studio";
    const logo = data.settings?.logoUrl;
    root.querySelectorAll("[data-csl-brand-name]").forEach((node) => { node.textContent = name; });
    if (logo) {
      root.querySelectorAll("[data-csl-logo]").forEach((node) => {
        node.src = logo;
        node.hidden = false;
        classifyLogo(node);
      });
      root.querySelectorAll("[data-csl-brand-name]").forEach((node) => { node.hidden = true; });
    }
    if (data.settings?.allowPersonalItems === false) {
      root.querySelectorAll('[data-csl-open="personal"]').forEach((node) => { node.hidden = true; });
    }
    renderPreview(root, data.library);
    syncPreviewState(root, "available");
  }

  function blankPreviewSlots(root) {
    return [...root.querySelectorAll("[data-csl-piece]")].some((slot) => !slot.querySelector("img")?.getAttribute("src") || slot.dataset.previewSlot === "error");
  }

  function previewUrl(root) {
    const params = new URLSearchParams();
    params.set("v", "transparent-composition-v2");
    if (root.dataset.initialCollectionHandle) params.set("collection_handle", root.dataset.initialCollectionHandle);
    if (root.dataset.previewCatalog) params.set("catalog", root.dataset.previewCatalog);
    const encoded = params.toString();
    const query = encoded ? `?${encoded}` : "";
    return `${root.dataset.apiBase || "/apps/coord-studio/api"}/preview${query}`;
  }

  async function hydratePreview(root) {
    if (!blankPreviewSlots(root)) return;
    root.dataset.previewState = "loading";
    const url = previewUrl(root);
    if (!previewRequests.has(url)) {
      previewRequests.set(url, fetch(url, { headers: { Accept: "application/json" } }).then(async (response) => {
        if (!response.ok) throw new Error("Preview unavailable");
        return response.json();
      }));
    }
    try {
      const data = await previewRequests.get(url);
      renderPreview(root, data.items);
      syncPreviewState(root, "available");
    } catch {
      previewRequests.delete(url);
      syncPreviewState(root, "unavailable");
      if (root.dataset.previewRetryBound !== "true") {
        root.dataset.previewRetryBound = "true";
        window.addEventListener("online", () => void hydratePreview(root), { once: true });
      }
    }
  }

  function schedulePreview(root) {
    if (!blankPreviewSlots(root)) return;
    if (!("IntersectionObserver" in window)) {
      window.setTimeout(() => void hydratePreview(root), 0);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      void hydratePreview(root);
    }, { rootMargin: "320px" });
    observer.observe(root);
  }

  async function getBootstrap(root) {
    if (root.__coordStudioBootstrap) return root.__coordStudioBootstrap;
    if (bootstrapRequests.has(root)) return bootstrapRequests.get(root);
    const seed = readSeed(root);
    if (seed) { renderBootstrap(root, seed); return seed; }
    const request = (async () => {
      const params = new URLSearchParams({ visitor_id: visitorId() });
      if (root.dataset.initialProductHandle) params.set("product_handle", root.dataset.initialProductHandle);
      if (root.dataset.initialCollectionHandle) params.set("collection_handle", root.dataset.initialCollectionHandle);
      if (root.dataset.previewCatalog) params.set("catalog", root.dataset.previewCatalog);
      const response = await fetch(`${root.dataset.apiBase || "/apps/coord-studio/api"}/bootstrap?${params.toString()}`, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error(root.dataset.messageLibraryUnavailable || "Shop products are temporarily unavailable");
      const data = await response.json();
      renderBootstrap(root, data);
      return data;
    })();
    bootstrapRequests.set(root, request);
    try {
      return await request;
    } catch (error) {
      bootstrapRequests.delete(root);
      throw error;
    }
  }

  function loadStyle(root) {
    const href = root.dataset.editorStyleSrc;
    if (!href) return Promise.reject(new Error('Missing studio stylesheet'));
    if (stylesheetRequests.has(href)) return stylesheetRequests.get(href);
    const request = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.csEditorStyle = '';
      link.onload = () => resolve();
      link.onerror = () => {
        link.remove();
        stylesheetRequests.delete(href);
        reject(new Error('Studio stylesheet unavailable'));
      };
      document.head.append(link);
    });
    stylesheetRequests.set(href, request);
    return request;
  }

  function loadEditor(root) {
    const source = root.dataset.editorSrc;
    if (!source) return Promise.reject(new Error(root.dataset.messageNotConfigured || "Coord Studio is not configured for this theme."));
    if (!editorModules.has(source)) editorModules.set(source, import(source).catch((error) => {
      editorModules.delete(source);
      throw error;
    }));
    return editorModules.get(source);
  }

  function updateDraftResume(root) {
    const button = root.querySelector("[data-csl-resume]");
    const shop = root.dataset.shopDomain;
    if (!button || !shop) return;
    try {
      button.hidden = !localStorage.getItem(`coord-studio-draft:${shop}`);
    } catch {
      button.hidden = true;
    }
  }

  function warmEditor(root) {
    void loadStyle(root).catch(() => {});
    void getBootstrap(root).catch(() => {});
    void loadEditor(root).catch(() => {});
  }

  function setOpening(root, opening) {
    const update = () => {
      root.setAttribute("aria-busy", String(opening));
      root.querySelectorAll("[data-csl-open]").forEach((button) => {
        button.disabled = opening;
      });
    };
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (opening && !reducedMotion && typeof document.startViewTransition === "function") {
      document.startViewTransition(update);
    } else {
      update();
    }
  }

  function waitForEditorPaint() {
    return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  }

  function currentProductHandle() {
    const match = window.location.pathname.match(/^\/products\/([^/?#]+)/i);
    if (!match) return "";
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }

  function setupInlineProductEntry(root) {
    if (root.dataset.entryKind !== "embed") return;
    const productHandle = root.dataset.initialProductHandle || currentProductHandle();
    if (!productHandle) return;
    root.dataset.initialProductHandle = productHandle;
    if (document.querySelector("[data-coord-studio-product-inline]")) return;
    const addButton = document.querySelector('product-form form [name="add"], form[action*="/cart/add"] [name="add"]');
    if (!(addButton instanceof HTMLElement)) return;
    const container = addButton.closest('[class*="product-form__buttons"]') || addButton.parentElement;
    if (!container) return;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "csqe-product-inline";
    button.dataset.coordStudioProductInline = productHandle;
    button.innerHTML = '<span><small>COORD STUDIO</small><strong>Style this piece</strong></span><b aria-hidden="true">↗</b>';
    button.addEventListener("click", () => void openEditor(root, "products", true));
    button.addEventListener("pointerenter", () => warmEditor(root), { once: true });
    button.addEventListener("focus", () => warmEditor(root), { once: true });
    container.insertBefore(button, addButton);
  }

  function setupPromotionCoordination(root) {
    if (root.id !== "coord-studio" || root.dataset.promotionCoordinationReady === "true") return;
    root.dataset.promotionCoordinationReady = "true";
    const markDiscountTab = () => {
      [...document.querySelectorAll("button, a, [role=button]")].forEach((node) => {
        if (/^GET\s+10%\s+OFF$/i.test((node.textContent || "").trim())) node.dataset.coordStudioPromotionTab = "true";
      });
    };
    const setActive = (active) => {
      document.documentElement.classList.toggle("coord-studio-entry-visible", active);
      if (active) markDiscountTab();
    };
    if (window.location.hash === "#coord-studio") setActive(true);
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => setActive(entries.some((entry) => entry.isIntersecting)), { threshold: .08 });
      observer.observe(root);
    }
    const mutations = new MutationObserver(() => {
      if (document.documentElement.classList.contains("coord-studio-entry-visible")) markDiscountTab();
    });
    mutations.observe(document.body, { childList: true, subtree: true });
  }

  async function openEditor(root, panel, restoreDraft = true, initialRecipeId) {
    if (root.getAttribute("aria-busy") === "true") return;
    const status = root.querySelector("[data-csl-status]");
    const errorMessageNode = root.closest('.lf-studio-card')?.querySelector('[data-studio-error-message]');
    const failedMessage = errorMessageNode ? JSON.parse(errorMessageNode.textContent) : root.dataset.messageOpenFailed;
    let timeout;
    setOpening(root, true);
    if (status) status.textContent = root.dataset.messageOpening || "Opening Coord Studio…";
    try {
      const [, , module] = await Promise.race([
        Promise.all([loadStyle(root), getBootstrap(root), loadEditor(root)]),
        new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error(failedMessage)), 20000); }),
      ]);
      module.mountCoordStudio(root, {
        entryKind: root.dataset.entryKind || undefined,
        initialPanel: panel,
        initialProductHandle: root.dataset.initialProductHandle || undefined,
        initialCollectionHandle: root.dataset.initialCollectionHandle || undefined,
        initialRecipeId,
        restoreDraft,
      });
      await waitForEditorPaint();
      if (status) status.textContent = "";
    } catch (error) {
      bootstrapRequests.delete(root);
      if (status) status.textContent = failedMessage || 'The studio could not open. Try again or refresh this page.';
    } finally {
      clearTimeout(timeout);
      setOpening(root, false);
    }
  }

  function setup(root) {
    if (root.dataset.coordStudioReady === "true") return;
    root.dataset.coordStudioReady = "true";
    root.querySelectorAll("[data-csl-logo]").forEach(classifyLogo);
    const seed = readSeed(root);
    if (seed) renderBootstrap(root, seed);
    else syncPreviewState(root);
    schedulePreview(root);
    setupPromotionCoordination(root);
    updateDraftResume(root);
    window.addEventListener("coord-studio:draft-saved", (event) => {
      if (!event.detail?.shop || event.detail.shop === root.dataset.shopDomain) updateDraftResume(root);
    });
    root.querySelectorAll("[data-csl-open]").forEach((button) => {
      button.addEventListener("click", () => openEditor(root, button.dataset.cslOpen || "products", button.dataset.cslRestoreDraft !== "false", VALID_RECIPE_IDS.has(button.dataset.cslRecipe) ? button.dataset.cslRecipe : undefined));
      if (root.dataset.warmOnIntent !== "false") {
        button.addEventListener("pointerenter", () => warmEditor(root), { once: true });
        button.addEventListener("focus", () => warmEditor(root), { once: true });
      }
    });
    setupInlineProductEntry(root);
    const query = new URL(window.location.href).searchParams;
    const autoPanel = root.dataset.autoOpenPanel || (query.get("coord") ? "products" : "");
    if (autoPanel && !document.documentElement.dataset.coordStudioAutoOpened) {
      document.documentElement.dataset.coordStudioAutoOpened = "true";
      window.setTimeout(() => void openEditor(root, autoPanel), 0);
    }
  }
})();
